import {
  Link,
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from 'react-router';
import {
  LayoutDashboard,
  Users,
  PieChart,
  FileText,
  Shield,
  Briefcase,
  UserCog,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  LogOut,
  Coins,
  type LucideIcon,
} from 'lucide-react';
import { UserProfileMenu } from '@/components/ui/UserProfileMenu';
import { useState, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { ShortcutIndicator } from '@/components/keyboard/ShortcutIndicator';
import { useAuth } from '@/hooks/useAuthHook';
import { adminNavigation } from '@/config/admin-navigation';
import {
  fundNavItems,
  companyNavItems,
  investorNavItems,
  fundsCarryNavItems,
  type EntityNavItem,
} from '@/config/entity-navigation';
import { EntitySidebarNav } from './EntitySidebarNav';
import dffLogo from '@/assets/dff-logo-blue.svg';
import {
  MANAGE_FUND_MEMBERS,
  VIEW_FUND_MEMBERS,
} from '@/constants/permissions';

interface WorkspaceNavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  shortcut: string;
  permissions?: string[];
}

const workspaceNavigation: WorkspaceNavItem[] = [
  {
    name: 'Dashboard',
    href: '/app/dashboard',
    icon: LayoutDashboard,
    shortcut: 'd',
  },
  { name: 'Funds', href: '/app/funds', icon: Briefcase, shortcut: 'f' },
  { name: 'Investors', href: '/app/investors', icon: Users, shortcut: 'i' },
  {
    name: 'Portfolio Companies',
    href: '/app/portfolio',
    icon: PieChart,
    shortcut: 'p',
  },
  { name: 'Reports', href: '/app/reports', icon: FileText, shortcut: 'r' },
  { name: 'Documents', href: '/app/documents', icon: FileText, shortcut: 'm' },
  { name: 'Compliance', href: '/app/compliance', icon: Shield, shortcut: 'c' },
  {
    name: 'Funds carry',
    href: '/app/funds-carry',
    icon: Coins,
    shortcut: 'g',
    permissions: [VIEW_FUND_MEMBERS, MANAGE_FUND_MEMBERS],
  },
];

const flattenedAdminNav = adminNavigation.flatMap((group) =>
  group.items.map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    href: `/app${item.path}`,
    group: group.label,
  }))
);

const groupedAdminNav = flattenedAdminNav.reduce<
  Array<{ group: string; items: typeof flattenedAdminNav }>
>((groups, item) => {
  const existing = groups.find((g) => g.group === item.group);
  if (existing) {
    existing.items.push(item);
  } else {
    groups.push({ group: item.group, items: [item] });
  }
  return groups;
}, []);

const isNavItemActive = (item: WorkspaceNavItem, pathname: string) =>
  pathname === item.href || pathname.startsWith(`${item.href}/`);

type EntitySidebarMode = 'fund' | 'company' | 'investor' | 'funds-carry';
type SidebarMode = 'main' | 'admin' | EntitySidebarMode;

function getSidebarMode(route: {
  isAdminPage: boolean;
  fundId?: string;
  companyId?: string;
  investorId?: string;
  isFundsCarry: boolean;
}): SidebarMode {
  if (route.isAdminPage) return 'admin';
  if (route.fundId) return 'fund';
  if (route.companyId) return 'company';
  if (route.investorId) return 'investor';
  if (route.isFundsCarry) return 'funds-carry';
  return 'main';
}

interface EntityNav {
  backHref: string;
  backLabel: string;
  items: EntityNavItem[];
  activeTab: string;
  activeSection?: string;
  activeSubsection?: string;
  buildHref: (tab: string, section?: string, subsection?: string) => string;
}

const buildFundsCarryHref = (section: string) => `/app/funds-carry/${section}`;

interface EntityModeMainMenuProps {
  items: WorkspaceNavItem[];
  pathname: string;
  expanded: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}

function EntityModeMainMenu({
  items,
  pathname,
  expanded,
  onToggle,
  onNavigate,
}: EntityModeMainMenuProps) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center px-3 py-2 text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider hover:text-muted-foreground transition-colors rounded-md hover:bg-accent/50"
      >
        <span className="flex-1 text-left">Main Menu</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform',
            expanded ? 'rotate-180' : ''
          )}
        />
      </button>

      {expanded && (
        <div className="space-y-0.5 mt-0.5">
          {items.map((item) => {
            const isActive = isNavItemActive(item, pathname);

            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onNavigate}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
              >
                <item.icon className="shrink-0 h-5 w-5 mr-3" />
                <span className="flex-1">{item.name}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface AdminSidebarNavProps {
  collapsed: boolean;
  currentAdminSection: string | null;
  onNavigate?: () => void;
}

function AdminSidebarNav({
  collapsed,
  currentAdminSection,
  onNavigate,
}: AdminSidebarNavProps) {
  return (
    <div className="space-y-2">
      <Link
        to="/app/dashboard"
        onClick={onNavigate}
        className={cn(
          'flex items-center px-3 py-2 rounded-md transition-colors group hover:bg-accent',
          collapsed ? 'justify-center' : ''
        )}
      >
        <ArrowLeft
          className={cn(
            'shrink-0 h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-x-0.5',
            collapsed ? '' : 'mr-2'
          )}
        />
        {!collapsed && (
          <span className="text-lg font-semibold text-foreground">
            Administrator
          </span>
        )}
      </Link>

      <div className="border-t border-border" />

      {groupedAdminNav.map((group, groupIndex) => (
        <div key={group.group}>
          {groupIndex > 0 && <div className="border-t border-border mb-2" />}
          {group.items.map((item) => {
            const isActive = item.id === currentAdminSection;
            return (
              <Link
                key={item.id}
                to={item.href}
                onClick={onNavigate}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  collapsed ? 'justify-center' : '',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
              >
                <item.icon
                  className={cn('shrink-0 h-5 w-5', collapsed ? '' : 'mr-3')}
                />
                {!collapsed && <span className="flex-1">{item.label}</span>}
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  );
}

interface SidebarProps {
  onNavigate?: () => void;
}

export default function Sidebar({ onNavigate }: SidebarProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [mainMenuExpanded, setMainMenuExpanded] = useState(false);
  const { user, logout, hasPermission } = useAuth();

  const visibleWorkspaceNavigation = workspaceNavigation.filter(
    (item) => !item.permissions || item.permissions.some(hasPermission)
  );

  const handleLogout = async () => {
    try {
      await logout();
      onNavigate?.();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const canAccessAdmin = user?.roles.some((role) =>
    ['super_admin', 'general_partner'].includes(role.name)
  );

  // --- Mode detection ---
  const isAdminPage = location.pathname.startsWith('/app/admin');
  const fundMatch = useMatch('/app/funds/:fundId/*');
  const fundReportsMatch = useMatch('/app/funds/:fundId/reports/*');
  const fundTemplatesMatch = useMatch('/app/funds/:fundId/reports/templates/*');
  const companyMatch = useMatch('/app/portfolio/:companyId/*');
  const investorRootMatch = useMatch('/app/investors/:investorId');
  const investorEditMatch = useMatch('/app/investors/:investorId/edit');
  const investorContactMatch = useMatch(
    '/app/investors/:investorId/contacts/*'
  );
  const investorMatch =
    investorRootMatch || investorEditMatch || investorContactMatch;
  const fundsCarryMatch = useMatch('/app/funds-carry/:section/*');

  const fundId = fundMatch?.params.fundId;
  const companyId = companyMatch?.params.companyId;
  const rawInvestorId = investorMatch?.params.investorId;
  // Guard: /investors/new is create-mode, not an existing entity
  const investorId = rawInvestorId !== 'new' ? rawInvestorId : undefined;

  const sidebarMode = getSidebarMode({
    isAdminPage,
    fundId,
    companyId,
    investorId,
    isFundsCarry: Boolean(fundsCarryMatch),
  });

  // --- URL param extraction ---
  const fundActiveTab = fundReportsMatch
    ? 'reports'
    : (searchParams.get('tab') ?? 'overview');
  // A report route carries no ?section, so name the one it belongs to.
  const fundReportsSection = fundTemplatesMatch ? 'templates' : 'list';
  const fundActiveSection = fundReportsMatch
    ? fundReportsSection
    : (searchParams.get('section') ?? '');
  const fundActiveSubsection = searchParams.get('subsection') ?? '';

  const companyActiveTab = searchParams.get('section') ?? 'overview';
  const companyActiveSection = searchParams.get('subsection') ?? '';

  const investorActiveTab = investorContactMatch
    ? 'contacts'
    : (searchParams.get('tab') ?? 'overview');

  const fundsCarryActiveTab = fundsCarryMatch?.params.section ?? '';

  const buildFundHref = useCallback(
    (tab: string, section?: string, subsection?: string) => {
      const params = new URLSearchParams({ tab });
      if (section) params.set('section', section);
      if (subsection) params.set('subsection', subsection);
      return `/app/funds/${fundId}?${params.toString()}`;
    },
    [fundId]
  );

  const buildCompanyHref = useCallback(
    (tab: string, section?: string) => {
      const params = new URLSearchParams({ section: tab });
      if (section) params.set('subsection', section);
      return `/app/portfolio/${companyId}?${params.toString()}`;
    },
    [companyId]
  );

  const buildInvestorHref = useCallback(
    (tab: string) => `/app/investors/${investorId}?tab=${tab}`,
    [investorId]
  );

  const entityNavByMode: Record<EntitySidebarMode, EntityNav> = {
    fund: {
      backHref: '/app/funds',
      backLabel: 'All Funds',
      items: fundNavItems,
      activeTab: fundActiveTab,
      activeSection: fundActiveSection,
      activeSubsection: fundActiveSubsection,
      buildHref: buildFundHref,
    },
    company: {
      backHref: '/app/portfolio',
      backLabel: 'Portfolio Companies',
      items: companyNavItems,
      activeTab: companyActiveTab,
      activeSection: companyActiveSection,
      buildHref: buildCompanyHref,
    },
    investor: {
      backHref: '/app/investors',
      backLabel: 'All Investors',
      items: investorNavItems,
      activeTab: investorActiveTab,
      buildHref: buildInvestorHref,
    },
    'funds-carry': {
      backHref: '/app/dashboard',
      backLabel: 'Funds carry',
      items: fundsCarryNavItems,
      activeTab: fundsCarryActiveTab,
      buildHref: buildFundsCarryHref,
    },
  };

  const entityNav =
    sidebarMode === 'main' || sidebarMode === 'admin'
      ? null
      : entityNavByMode[sidebarMode];

  useKeyboardShortcuts('sidebar', [
    ...visibleWorkspaceNavigation.map((item) => ({
      key: item.shortcut,
      description: `Navigate to ${item.name}`,
      action: () => navigate(item.href),
      category: 'Navigation',
    })),
    {
      key: 'b',
      description: 'Toggle sidebar',
      action: () => setCollapsed((c) => !c),
      category: 'Navigation',
    },
    {
      key: 'q',
      description: 'Sign out',
      action: handleLogout,
      category: 'Navigation',
    },
  ]);

  const getCurrentAdminSection = () => {
    const match = location.pathname.match(/\/app\/admin\/([^/]+)/);
    return match ? match[1] : null;
  };
  const currentAdminSection = getCurrentAdminSection();

  return (
    <div
      className={cn(
        'bg-background border-r border-border flex flex-col h-full transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        {!collapsed && (
          <Link to="/app/dashboard" className="flex items-end gap-1">
            <img src={dffLogo} alt="DFF" className="h-6 w-auto mb-0.5" />
            <span className="text-lg leading-none">Flow</span>
          </Link>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded-md hover:bg-accent"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {/* MAIN mode */}
        {sidebarMode === 'main' && (
          <>
            <div className="space-y-0.5">
              {visibleWorkspaceNavigation.map((item) => {
                const isActive = isNavItemActive(item, location.pathname);

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={onNavigate}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      collapsed ? 'justify-center' : '',
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    )}
                  >
                    <item.icon
                      className={cn(
                        'shrink-0 h-5 w-5',
                        collapsed ? '' : 'mr-3'
                      )}
                    />
                    {!collapsed && (
                      <>
                        <span className="flex-1">{item.name}</span>
                        <ShortcutIndicator
                          shortcut={{ key: item.shortcut }}
                          variant="compact"
                          className="ml-auto"
                        />
                      </>
                    )}
                  </Link>
                );
              })}
            </div>

            <div className="my-4 border-t border-border" />

            {canAccessAdmin && (
              <Link
                to="/app/admin/users"
                onClick={onNavigate}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  collapsed ? 'justify-center' : '',
                  'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
              >
                <UserCog
                  className={cn('shrink-0 h-5 w-5', collapsed ? '' : 'mr-3')}
                />
                {!collapsed && (
                  <>
                    <span className="flex-1">Admin</span>
                    <ShortcutIndicator
                      shortcut={{ key: 'a' }}
                      variant="compact"
                      className="ml-auto"
                    />
                  </>
                )}
              </Link>
            )}
          </>
        )}

        {/* ENTITY modes: fund / company / investor / funds carry */}
        {entityNav && (
          <div className="space-y-1">
            {/* Global workspace nav - collapsible in entity mode */}
            {!collapsed && (
              <EntityModeMainMenu
                items={visibleWorkspaceNavigation}
                pathname={location.pathname}
                expanded={mainMenuExpanded}
                onToggle={() => setMainMenuExpanded((v) => !v)}
                onNavigate={onNavigate}
              />
            )}

            <div className="border-t border-border pt-1" />

            {/* Back link */}
            <Link
              to={entityNav.backHref}
              onClick={onNavigate}
              className={cn(
                'flex items-center px-3 py-2 rounded-md transition-colors group hover:bg-accent',
                collapsed ? 'justify-center' : ''
              )}
            >
              <ArrowLeft
                className={cn(
                  'shrink-0 h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-x-0.5',
                  collapsed ? '' : 'mr-2'
                )}
              />
              {!collapsed && (
                <span className="text-md font-semibold text-foreground">
                  {entityNav.backLabel}
                </span>
              )}
            </Link>

            {/* Entity sub-navigation */}
            <EntitySidebarNav
              showBackButton={false}
              backHref=""
              backLabel=""
              items={entityNav.items}
              activeTab={entityNav.activeTab}
              activeSection={entityNav.activeSection ?? ''}
              activeSubsection={entityNav.activeSubsection ?? ''}
              collapsed={collapsed}
              buildHref={entityNav.buildHref}
              onItemClick={onNavigate}
            />
          </div>
        )}

        {/* ADMIN mode */}
        {sidebarMode === 'admin' && (
          <AdminSidebarNav
            collapsed={collapsed}
            currentAdminSection={currentAdminSection}
            onNavigate={onNavigate}
          />
        )}
      </nav>

      {/* Footer */}
      <div className="p-2 border-t border-border">
        <UserProfileMenu user={user ?? null} collapsed={collapsed} />
        <button
          onClick={handleLogout}
          className={cn(
            'w-full flex items-center px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground rounded-md transition-colors mt-1',
            collapsed ? 'justify-center' : 'gap-3'
          )}
          title="Sign out"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );
}
